import React from "react";
import { makeStyles } from "@mui/styles";
import { Link } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import LaunchIcon from '@mui/icons-material/Launch';
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import clsx from 'clsx';
import _ from "lodash";
import EloChange from '../common/EloChange';
import Rating3Badge from '../common/Rating3Badge';
import ImpactRatingBadge from '../common/ImpactRatingBadge';
import MatchOutcomeSummary from "./MatchOutcomeSummary";
import utils from "../../universal/utils";
import { getMapMetaById } from "../../constants/mapMeta";
import { buildMatchDemoDownloadUrl, canShowMatchDemoDownload } from "../match/matchDemoDownload";

const useStyles = makeStyles({
  containerScrollBar: {
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#1C1E20',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#797979',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#888',
    },

    // Firefox support
    scrollbarWidth: 'thin', 
    scrollbarColor: '#797979 #1C1E20',
  },
  table: {
    minWidth: 650,
    width: "100%",
  },
  tableHeadCell: {
    background: "#1C1E20 !important",
    fontSize: "16px",
    fontWeight: "bold",
  },
  noMatchesPlayed: {
    color: "#797979",
  },
  tableRows: {
    fontWeight: "600",
    background: "#2A2F34",
    '&:hover': {
      backgroundColor: '#3A3F45',
    },
  },
  tableRowWin: {
    backgroundColor: 'rgba(56, 176, 0, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(56, 176, 0, 0.1)',
    },
  },
  tableRowLoss: {
    backgroundColor: 'rgba(217, 4, 41, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(217, 4, 41, 0.1)',
    },
  },
  cellWin: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#38b000",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  cellLoss: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#d90429",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  resultCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.35rem",
  },
  resultAnnotation: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#b5b1b3",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1.1,
  },
  buttonViewMatch: {
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    boxShadow: "none",
    background: "none",
    color: "#ffffff",
    '&:hover': {
      backgroundColor: "transparent",
      boxShadow: "none",
    },
    '&:hover $labelMoreInfoIcon': {
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      background: "#E8A33D",
    },
    '&:hover $labelMoreInfoIcon $moreInfoIcon': {
      color: "#ffffff",
    }
  },
  labelMoreInfoIcon: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#797979",
    padding: "0.5rem",
    borderRadius: "50%",
    transition: "background-color 0.4s ease",
  },
  tooltipContent: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    marginBottom: "0.5rem",
  },
  moreInfoIcon: {
    width: "1.3rem", 
    height: "1.3rem", 
    marginLeft: "0", 
    color: "#2A2F34",
    transition: "color 0.4s ease",
  },
  actionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.35rem",
  },

  //Maps Classes
  de_mirage: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#d94f3b44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_nuke: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#4ae0d244",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_ancient: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#2cb34a44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_dust_ii: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#f0b36c44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_vertigo: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#ffe24a44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_inferno: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#5a8fff44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_anubis: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#47cc8c44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_cache: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#7f8c8d44",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
  de_cobblestone_2018: {
    position: "relative",
    fontWeight: "500",
    backgroundColor: "#8d6e6344",
    padding: "0.2rem 0.5rem",
    borderRadius: "4px",
  },
});

function getMapDisplay(mapId) {
  return getMapMetaById(mapId)?.display || mapId || "Unknown Map";
}

function getSortedMatchData(rows = {}, sortOptions = {}) {

  const matchData = Object.entries(rows).map(([matchId, data]) => ({
    matchId,
    ...data,
    humanDate: format(new Date(data.timestamp), "dd MMM, yyyy")
  }));

  const { general: generalSort, map: mapSort } = sortOptions;

  let filteredData = matchData;
  if (mapSort && mapSort.toLowerCase() !== 'all maps') {
    filteredData = filteredData.filter(row =>
      getMapDisplay(row.map).toLowerCase() === mapSort.toLowerCase()
    );
  }

  switch (generalSort?.toLowerCase()) {
    case 'win':
      return _.orderBy(
        filteredData.filter(row => row.outcome === 'Win'),
        ['timestamp'],
        ['desc']
      );
    case 'loss':
      return _.orderBy(
        filteredData.filter(row => row.outcome === 'Loss'),
        ['timestamp'],
        ['desc']
      );
    case 'date':
    default:
      return _.orderBy(filteredData, ['timestamp'], ['desc']);
  }
}

export default function MatchHistoryTable({
  rows,
  areMatchesAvailable,
  sortOptions,
  hideSort = false,
  isLoggedIn = false,
}) {
  const classes = useStyles();
  const edgeBasePath = utils.getEdgeBasePath();

  const isRowsValid = rows && typeof rows === 'object' && Object.keys(rows).length > 0;
  const isSortOptionsValid = sortOptions && typeof sortOptions === 'object';

  if (!isRowsValid || !isSortOptionsValid) {
    return (
      <div style={{width: "100%", textAlign: "center"}}>
        <Typography variant="body1" color="textSecondary">
          No valid match data available.
        </Typography>
      </div>
    );
  }

  const { general: generalSort, map: mapSort } = sortOptions;

  const sortedRows = getSortedMatchData(rows, sortOptions);
  const isScrollable = sortedRows.length > 5;

  return (
    <TableContainer component={Paper} className={classes.containerScrollBar} style={isScrollable ? { maxHeight: 465, overflowY: "auto" } : {}}>
      <Table className={classes.table} stickyHeader={isScrollable}>
        <TableHead style={{ background: "#1C1E20", position: "sticky", top: 0, zIndex: 2}}>
          <TableRow>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Date</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Result</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Score</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Map</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>K/D/A</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>ADR</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Rating</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>ELO</TableCell>
            <TableCell align="center" className={clsx(classes.tableHeadCell, { [classes.noMatchesPlayed]: !areMatchesAvailable })}>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!areMatchesAvailable ? (
            <TableRow>
              <TableCell colSpan={9} align="center" style={{padding: "20px", fontSize: "16px", backgroundColor: "#2A2F34",}}>
                Start playing to see your matches stats here!
              </TableCell>
            </TableRow>
          ) : sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" style={{padding: "20px", fontSize: "16px", backgroundColor: "#2A2F34",}}>
                  {(() => {
                    const getMapText = (mapValue) => {
                      if (mapValue === "all maps") return "";
                      return ` on ${mapValue}`;
                    };

                    switch (generalSort.toLowerCase()) {
                      case "win":
                        return `No matches won${getMapText(mapSort)}`;
                      case "loss":
                        return `No matches lost${getMapText(mapSort)}`;
                      case "date":
                      default:
                        return `No matches found${getMapText(mapSort)}`;
                    }
                  })()}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row) => {
              const rowClassName = clsx(classes.tableRows, {
                [classes.tableRowWin]: row.outcome === 'Win',
                [classes.tableRowLoss]: row.outcome === 'Loss',
              });

              return (
                <TableRow key={row.matchId} className={rowClassName}>
                  <TableCell align="center">{row.humanDate}</TableCell>
                  <TableCell align="center">
                    <div className={classes.resultCell}>
                      <span
                        className={
                          row.outcome === "Win" ? classes.cellWin : classes.cellLoss
                        }
                      >
                        {row.outcome}
                      </span>
                      {row.endedBy === "surrender" && row.endedByLabel ? (
                        <Typography className={classes.resultAnnotation}>
                          {row.endedByLabel}
                        </Typography>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {row.playerInTeam === "team1" ? `${row.team1Score} - ${row.team2Score}` 
                      : `${row.team2Score} - ${row.team1Score}`}
                  </TableCell>
                  <TableCell align="center">
                    <span className={`${classes[row.map]}`}>
                      {getMapDisplay(row.map)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {row.kills || 0} / {row.deaths || 0} / {row.assists || 0}
                  </TableCell>
                  <TableCell align="center">{row.adr.toFixed(1)}</TableCell>
                  <TableCell align="center">
                    <Rating3Badge value={row.rating3} size="small" />
                    <ImpactRatingBadge value={row.impactRating} size="small" />
                    <MatchOutcomeSummary summary={row.outcomeSummary} />
                  </TableCell>
                  <TableCell align="center">
                    {row.eloChange !== undefined && row.eloAfter !== undefined ? (
                      <EloChange
                        value={row.eloChange}
                        result={row.outcome}
                        currentElo={row.eloAfter}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.9rem', color: '#999999' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionsCell}>
                      <Button
                        to={`/match/${row.matchId}`}
                        component={Link}
                        variant="contained"
                        className={classes.buttonViewMatch}
                      >
                        <Tooltip
                          title="View Match"
                          placement="top"
                          classes={{
                            tooltip: classes.tooltipContent,
                          }}
                        >
                          <span className={classes.labelMoreInfoIcon}>
                            <LaunchIcon className={classes.moreInfoIcon} />
                          </span>
                        </Tooltip>
                      </Button>
                      {canShowMatchDemoDownload({
                        isLoggedIn,
                        demoAvailable: row.demoAvailable,
                      }) && (
                        <Button
                          component="a"
                          href={buildMatchDemoDownloadUrl({
                            matchId: row.matchId,
                            edgeBasePath,
                          })}
                          variant="contained"
                          className={classes.buttonViewMatch}
                        >
                          <Tooltip
                            title="Download Demo"
                            placement="top"
                            classes={{
                              tooltip: classes.tooltipContent,
                            }}
                          >
                            <span className={classes.labelMoreInfoIcon}>
                              <DownloadIcon className={classes.moreInfoIcon} />
                            </span>
                          </Tooltip>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
